import { useRef, useState, useCallback, useContext, useMemo } from 'react';

import Analytics from '@hh.ru/analytics-js';
import { Button, TooltipHover, Alert } from '@hh.ru/magritte-ui';
import { ExclamationTriangleFilledSize24 } from '@hh.ru/magritte-ui/icon';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';

import getResume from 'src/api/applicant/getResume';
import openContacts from 'src/api/resume/openContacts.obfuscate';
import { useNotification } from 'src/components/Notifications/Provider';
import { ResumeCardActionsContext } from 'src/components/ResumeCard/Actions/Context';
import translation from 'src/components/translation';
import useExperiment from 'src/hooks/useExperiment';
import { useSelector } from 'src/hooks/useSelector';
import useToggleState from 'src/hooks/useToggleState';
import { Permission } from 'src/models/resume/resumeCommon.types';
import { UserType } from 'src/models/userType';

import NotificationComponent from 'src/components/ResumeCard/Actions/OpenContacts/NotificationComponent';

const TrlKeys = {
    buttonText: 'resumeCard.openContacts.button.text',
    tooltipText: 'resumeCard.openContacts.tooltip.text',
    alertTitle: 'resumeCard.openContacts.alert.permissionProblem.title',
    alertText: 'resumeCard.openContacts.alert.permissionProblem.text',
    alertButtonText: 'resumeCard.openContacts.alert.permissionProblem.button.text',
};

/**
 * Кнопка CIV-открытия контактов на резюме.
 */
const OpenContacts: TranslatedComponent = ({ trls }) => {
    const { resume, vacancyId, onResumeUpdate } = useContext(ResumeCardActionsContext);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isAlertVisible, toggleIsAlertVisible] = useToggleState(false);
    const buttonRef = useRef(null);

    const { addNotification } = useNotification();
    const additionalCheck = useSelector(({ additionalCheck }) => additionalCheck);
    const userType = useSelector(({ userType }) => userType);
    const currentDivisionId = useSelector((state) => state.employerCurrentDivision?.divisionId);
    const isExpActive = useExperiment('resume_card_contacts_opening');

    const {
        _attributes: { hash: resumeHash, id: resumeId, permission },
        topicId,
        resumeContactsOpening,
        canOpenContacts,
    } = resume;
    const isEmployer = userType === UserType.Employer;
    const hasCivToOpenContacts = !!resumeContactsOpening?.throughCiv;
    const contactsAlreadyOpened = permission !== Permission.ViewWithoutContacts;

    const analyticsData = useMemo(
        () => ({
            goal: 'OpenContacts',
            resumeId,
            vacancyId,
        }),
        [resumeId, vacancyId]
    );

    const handleContactsOpen = useCallback(
        (event: React.MouseEvent) => {
            setIsProcessing(true);

            openContacts({
                divisionId: currentDivisionId,
                resumeHash,
                resumeId,
                event,
            })
                .then(({ data }) => {
                    if (data?.errorKey) {
                        addNotification(NotificationComponent, { props: { isError: true } });
                        setIsProcessing(false);
                        return;
                    }

                    Analytics.sendHHEvent('resumeShowContacts', analyticsData);

                    if (data?.contactsOpened) {
                        getResume({
                            resumeHash,
                            resumeId,
                            topicId,
                            vacancyId,
                        })
                            .then((updatedResume) => {
                                onResumeUpdate?.(updatedResume.resume);
                                addNotification(NotificationComponent);
                            })
                            .catch((e: Error) => {
                                Analytics.sendHHEvent('openContactsError', {
                                    ...analyticsData,
                                    errorName: e.name,
                                    errorMessage: e.message,
                                });

                                addNotification(NotificationComponent, { props: { isError: true } });
                            })
                            .finally(() => {
                                setIsProcessing(false);
                            });
                    }
                })
                .catch((e: Error) => {
                    setIsProcessing(false);

                    Analytics.sendHHEvent('openContactsError', {
                        ...analyticsData,
                        errorName: e.name,
                        errorMessage: e.message,
                    });

                    addNotification(NotificationComponent, { props: { isError: true } });
                });
        },
        [addNotification, analyticsData, currentDivisionId, onResumeUpdate, resumeHash, resumeId, topicId, vacancyId]
    );

    const clickHandler = canOpenContacts ? handleContactsOpen : toggleIsAlertVisible;

    if (!isExpActive || !isEmployer || additionalCheck || contactsAlreadyOpened || !hasCivToOpenContacts) {
        return null;
    }

    return (
        <>
            <Button
                ref={buttonRef}
                size="small"
                mode="primary"
                style="accent"
                onClick={clickHandler}
                loading={isProcessing}
                data-qa="resume-card-open-contacts"
            >
                {trls[TrlKeys.buttonText]}
            </Button>
            <TooltipHover activatorRef={buttonRef} placement="top-center" size="small">
                {trls[TrlKeys.tooltipText]}
            </TooltipHover>
            <Alert
                title={trls[TrlKeys.alertTitle]}
                description={trls[TrlKeys.alertText]}
                icon={<ExclamationTriangleFilledSize24 />}
                iconStyle="warning"
                layout="horizontal"
                visible={isAlertVisible}
                buttons={
                    <Button mode="secondary" style="accent" size="medium" onClick={toggleIsAlertVisible} stretched>
                        {trls[TrlKeys.alertButtonText]}
                    </Button>
                }
                onClose={toggleIsAlertVisible}
            />
        </>
    );
};

export default translation(OpenContacts);
